import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { posApiFetch } from "@/utils/pos-api";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2 } from "lucide-react";

// --- Form state ---

interface ModifierGroupFormState {
  name: string;
  displayName: string;
  description: string;
  selectionType: "single" | "multiple";
  required: boolean;
  minSelections: string;
  maxSelections: string;
  active: boolean;
  displayOrder: string;
}

const EMPTY_FORM: ModifierGroupFormState = {
  name: "",
  displayName: "",
  description: "",
  selectionType: "single",
  required: false,
  minSelections: "0",
  maxSelections: "",
  active: true,
  displayOrder: "0",
};

// --- Component ---

interface ModifierGroupEditorProps {
  modifierGroupId?: string;
  onBack: () => void;
}

export default function ModifierGroupEditor({ modifierGroupId, onBack }: ModifierGroupEditorProps) {
  const isEdit = !!modifierGroupId;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<ModifierGroupFormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  // --- Fetch existing modifier group for edit ---

  const { data: existingGroup, isLoading: isLoadingGroup } = useQuery<any>({
    queryKey: ["/v1/pos/modifier-groups", modifierGroupId],
    queryFn: async () => {
      const res = await posApiFetch(`/v1/pos/modifier-groups/${modifierGroupId}`);
      if (!res.ok) throw new Error("Failed to fetch modifier group");
      return res.json();
    },
    enabled: isEdit,
  });

  // Populate form
  useEffect(() => {
    if (!existingGroup) return;
    const g = existingGroup;
    setForm({
      name: g.name ?? "",
      displayName: g.displayName ?? "",
      description: g.description ?? "",
      selectionType: (g.selectionType as "single" | "multiple") ?? "single",
      required: g.required ?? false,
      minSelections: g.minSelections != null ? String(g.minSelections) : "0",
      maxSelections: g.maxSelections != null ? String(g.maxSelections) : "",
      active: g.active ?? true,
      displayOrder: g.displayOrder != null ? String(g.displayOrder) : "0",
    });
  }, [existingGroup]);

  // --- Field updater ---

  const set = <K extends keyof ModifierGroupFormState>(key: K, value: ModifierGroupFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // --- Submit ---

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast({ title: "Validation Error", description: "Internal name is required", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      const body: Record<string, any> = {
        name: form.name.trim(),
        displayName: form.displayName.trim() || form.name.trim(),
        description: form.description.trim() || null,
        selectionType: form.selectionType,
        required: form.required,
        minSelections: form.minSelections ? Number(form.minSelections) : 0,
        maxSelections: form.maxSelections ? Number(form.maxSelections) : null,
        active: form.active,
        displayOrder: form.displayOrder ? Number(form.displayOrder) : 0,
      };

      const url = isEdit ? `/v1/pos/modifier-groups/${modifierGroupId}` : "/v1/pos/modifier-groups";
      const method = isEdit ? "PUT" : "POST";

      const res = await posApiFetch(url, {
        method,
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || `Failed to ${isEdit ? "update" : "create"} modifier group`);
      }

      toast({ title: `Modifier group ${isEdit ? "updated" : "created"} successfully` });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/modifier-groups"] });
      onBack();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || `Failed to ${isEdit ? "update" : "create"} modifier group`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Loading ---

  if (isEdit && isLoadingGroup) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to Modifier Groups
          </Button>
        </div>
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--secondary-text-color)]" />
        </div>
      </div>
    );
  }

  // --- Render ---

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Modifier Groups
        </Button>
        <h2 className="text-[16px] font-semibold text-[var(--primary-text-color)]">
          {isEdit ? "Edit Modifier Group" : "Create Modifier Group"}
        </h2>
        <div className="w-[140px]" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-[900px] mx-auto pb-8">
        {/* Basic Details */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Basic Details</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Internal Name *</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. temperature, size, addons"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Display Name</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  value={form.displayName}
                  onChange={(e) => set("displayName", e.target.value)}
                  placeholder="e.g. How would you like it cooked?"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[12px]">Description</Label>
              <Textarea
                className="text-[13px] min-h-[60px]"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Describe this modifier group..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Selection Settings */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Selection Settings</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Selection Type</Label>
                <Select value={form.selectionType} onValueChange={(v) => set("selectionType", v as "single" | "multiple")}>
                  <SelectTrigger className="h-[36px] text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single Selection (Radio)</SelectItem>
                    <SelectItem value="multiple">Multiple Selection (Checkbox)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[var(--secondary-text-color)]">
                  {form.selectionType === "single"
                    ? "Customer can select only one option"
                    : "Customer can select multiple options"}
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Display Order</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="number"
                  min="0"
                  step="1"
                  value={form.displayOrder}
                  onChange={(e) => set("displayOrder", e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>

            {form.selectionType === "multiple" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-[12px]">Min Selections</Label>
                  <Input
                    className="h-[36px] text-[13px]"
                    type="number"
                    min="0"
                    step="1"
                    value={form.minSelections}
                    onChange={(e) => set("minSelections", e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[12px]">Max Selections</Label>
                  <Input
                    className="h-[36px] text-[13px]"
                    type="number"
                    min="0"
                    step="1"
                    value={form.maxSelections}
                    onChange={(e) => set("maxSelections", e.target.value)}
                    placeholder="Unlimited"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status & Requirements */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Status & Requirements</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="flex items-center gap-2">
              <Switch checked={form.required} onCheckedChange={(v) => set("required", v)} />
              <Label className="text-[12px]">Required</Label>
            </div>
            <p className="text-[11px] text-[var(--secondary-text-color)]">
              {form.required
                ? "Customer must make a selection from this group"
                : "Selection is optional"}
            </p>
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} />
              <Label className="text-[12px]">Active</Label>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            className="h-[36px] text-[13px]"
            onClick={onBack}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="h-[36px] text-[13px] bg-[var(--primary-color)] text-white hover:bg-[#9f0e0e]"
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEdit ? "Update Modifier Group" : "Create Modifier Group"}
          </Button>
        </div>
      </form>
    </div>
  );
}
